import { db } from "@/lib/firebase";
import { formatFirestoreDocument } from "@/lib/firestore";
import { Team } from "@/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useEffect, useMemo, useRef } from "react";
import { usePageVisibility } from "./useVisibilityPausedQuery";

/**
 * Real-time hook for fetching teams in a tournament
 * Uses Firestore onSnapshot for instant updates when teams change
 *
 * @param tournamentId - The ID of the tournament
 * @param activeOnly - If true, only fetch active teams (default: true)
 * @returns TanStack Query result with teams array
 */
export function useTournamentTeams(tournamentId: string, activeOnly = true) {
  const queryClient = useQueryClient();
  const isVisible = usePageVisibility();
  // Include activeOnly in queryKey to prevent cache collisions between admin/public views
  // Memoize queryKey to prevent re-subscribing on every render
  const queryKey = useMemo(
    () => ["teams", tournamentId, { activeOnly }] as const,
    [tournamentId, activeOnly],
  );

  // Ref to resolve the initial queryFn promise from onSnapshot
  const pendingRef = useRef<{
    resolve: (teams: Team[]) => void;
    reject: (error: Error) => void;
  } | null>(null);

  // Set up real-time listener that continuously updates the cache
  // Pauses when page is hidden to reduce Firestore costs
  useEffect(() => {
    // Don't subscribe when page is hidden
    if (!isVisible) return;

    const teamsQuery = query(
      collection(db, "teams"),
      where("tournamentId", "==", tournamentId),
      ...(activeOnly ? [where("isActive", "==", true)] : []),
    );

    const unsubscribe = onSnapshot(
      teamsQuery,
      (snapshot) => {
        const teams = snapshot.docs.map((doc) =>
          formatFirestoreDocument(doc),
        ) as Team[];

        // Resolve pending queryFn promise if waiting
        if (pendingRef.current) {
          pendingRef.current.resolve(teams);
          pendingRef.current = null;
        }

        // Update the query cache with new data
        queryClient.setQueryData(queryKey, teams);
      },
      (error) => {
        console.error("Error fetching teams:", error);
        // Reject pending promise if waiting
        if (pendingRef.current) {
          pendingRef.current.reject(error as Error);
          pendingRef.current = null;
        }
      },
    );

    return () => {
      unsubscribe();
      // Clean up pending promise on unmount - resolve with empty array instead of rejecting
      // to prevent React Query from treating this as an error
      if (pendingRef.current) {
        // Check if there's cached data to use, otherwise use empty array
        const cached = queryClient.getQueryData<Team[]>(queryKey);
        pendingRef.current.resolve(cached ?? []);
        pendingRef.current = null;
      }
    };
  }, [tournamentId, activeOnly, queryClient, queryKey, isVisible]);

  // Use the query to provide loading states and cached data
  return useQuery({
    queryKey,
    queryFn: () => {
      return new Promise<Team[]>((resolve, reject) => {
        // Check if data already exists in cache
        const cached = queryClient.getQueryData<Team[]>(queryKey);
        if (cached !== undefined) {
          resolve(cached);
          return;
        }

        // Set up timeout to prevent hanging promises
        // If onSnapshot hasn't fired within 10 seconds, resolve with empty array
        const timeoutId = setTimeout(() => {
          if (pendingRef.current) {
            console.warn(
              "useTournamentTeams: Timeout waiting for initial data",
            );
            pendingRef.current.resolve([]);
            pendingRef.current = null;
          }
        }, 10000);

        // Store resolve/reject with timeout cleanup
        const originalResolve = resolve;
        const originalReject = reject;

        pendingRef.current = {
          resolve: (teams) => {
            clearTimeout(timeoutId);
            originalResolve(teams);
          },
          reject: (error) => {
            clearTimeout(timeoutId);
            originalReject(error);
          },
        };
      });
    },
    staleTime: Infinity, // Real-time listener keeps it fresh
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes after unmount
  });
}
